import React, { memo } from 'react';
import { ErrorRow, LoadingPlaceholderRows, LoadMoreRow, NoResultsRow } from '../../search-status-rows';
import classNames from 'classnames';

export interface SearchResultColumn {
  name: string | undefined;
  customClassNames?: string | undefined;
  renderComponent?: React.ReactNode; // for sortable columns or with inline messages, etc
  ariaLabel?: string; // for accessibility
}

interface LoadMore {
  showLoadMore: boolean;
  onLoadMoreClick?: () => void;
  resultsCount?: number;
  totalCount?: number;
}

interface TableChildrenProps {
  columns?: SearchResultColumn[];
  children?: React.ReactNode;
}

export interface TableProps {
  id: string;
  columns?: SearchResultColumn[];
  children: React.ReactNode;
  noResults: boolean;
  isLoading: boolean;
  isFetchingMore?: boolean;
  error: string | null | undefined;
  entityName: string;
  loadMore?: LoadMore;
  colSpan?: number;
  additionalClassNames?: string;
  heading?: string;
  filter?: React.ReactNode;
  selectableResults?: boolean;
}

export const ColGroup = ({ columns, children }: TableChildrenProps) => {
  return (
    <colgroup>
      {columns &&
        columns.map(({ customClassNames = 'col-auto' }: SearchResultColumn, index) => {
          return <col className={customClassNames} key={index} />;
        })}
      {!columns && children}
    </colgroup>
  );
};

// False warning: https://github.com/yannickcr/eslint-plugin-react/issues/2105
// eslint-disable-next-line react/display-name
export const TableHead = memo(({ columns, children }: TableChildrenProps) => {
  return (
    <thead>
      <tr>
        {columns &&
          columns.map(({ name, renderComponent, ariaLabel }: SearchResultColumn, index: number) => (
            <React.Fragment key={index}>
              {renderComponent ? (
                renderComponent
              ) : (
                <th key={index} aria-label={ariaLabel}>
                  {renderComponent ? renderComponent : name}
                </th>
              )}
            </React.Fragment>
          ))}

        {!columns && children}
      </tr>
    </thead>
  );
});

export const TableBody = ({ children }: TableChildrenProps) => {
  return <tbody>{children}</tbody>;
};

// False warning: https://github.com/yannickcr/eslint-plugin-react/issues/2105
// eslint-disable-next-line react/display-name
export const SearchResultsTable = memo(
  ({
    id,
    additionalClassNames,
    children,
    noResults,
    isLoading,
    isFetchingMore = false,
    error,
    entityName,
    colSpan = 100,
    heading,
    filter,
    loadMore = {
      showLoadMore: false,
    },
    selectableResults = true,
  }: TableProps) => {
    const { showLoadMore, onLoadMoreClick, resultsCount, totalCount } = loadMore;
    return (
      <>
        <h2>
          {heading} {noResults}
        </h2>
        {filter && (
          <div className="search-table-header" data-testid="search-filter">
            {filter}
          </div>
        )}
        <table
          id={id}
          className={classNames(`${additionalClassNames} aui table-list table-search-results`, {
            'selectable-results': selectableResults,
          })}
          data-testid={`search-results-table-${id}`}
        >
          {/* TODO: Cleanup table logic to not need so many conditionals */}
          {!noResults && children}
          {noResults && !isLoading && !error && <NoResultsRow colSpan={colSpan} entitiesName={entityName} />}
          {(isLoading || isFetchingMore) && <LoadingPlaceholderRows colSpan={colSpan} numberOfRows={3} />}
          {error && <ErrorRow colSpan={colSpan} error={error} />}
          {showLoadMore && (
            <LoadMoreRow
              colSpan={colSpan}
              onLoadMoreClick={onLoadMoreClick || (() => null)}
              resultsCount={resultsCount}
              totalHits={totalCount || 0}
            />
          )}
        </table>
      </>
    );
  }
);
